/**
 * Contract for the admin coach directory at `/admin/coaches`: the facet
 * vocabularies, the list and export inputs, and the row and summary shapes.
 * Client-safe. Every state named here is derived in one place — the SQL in
 * `~/lib/server/admin-coach-directory` — so a filter, a summary count, and the
 * badge on a row can never disagree about what "listed" or "paying" means.
 *
 * Aliases: admin coach directory, coach overview table, admin coach list,
 * coach export, coach segments, coach status flags
 */

import { z } from "zod";

import type { MembershipPlan } from "~/lib/schemas/db-enums";
import type { PlanKey } from "~/lib/shared/plan-catalogue";
import { SORT_DIRECTIONS } from "~/lib/shared/sort";

/** Tier 1 approval: may this person use the platform as a coach at all. */
export const ACCOUNT_STATUSES = ["approved", "pending", "revoked"] as const;
export type AccountStatus = (typeof ACCOUNT_STATUSES)[number];

/**
 * Tier 2 approval: marketplace visibility. `hidden` is approved for the
 * marketplace but the coach's profile is set to private.
 */
export const LISTING_STATUSES = [
  "listed",
  "hidden",
  "inReview",
  "returned",
  "revoked",
  "notSubmitted",
] as const;
export type ListingStatus = (typeof LISTING_STATUSES)[number];

/**
 * Where a coach stands with Nova billing. `canceling` keeps access until the
 * period ends; `billingIssue` is any Stripe status that needs the coach to fix
 * payment; `freeAccess` is free NOVA Core from staff with no Stripe plan above
 * it; `lapsed` once trialed or paid and has no subscription now.
 */
export const NOVA_STATES = [
  "paying",
  "trialing",
  "canceling",
  "billingIssue",
  "freeAccess",
  "lapsed",
  "never",
] as const;
export type NovaState = (typeof NOVA_STATES)[number];

/** How the profile came to exist: signed up, or pre-generated from public data. */
export const COACH_SOURCES = ["organic", "aiProfile"] as const;
export type CoachSource = (typeof COACH_SOURCES)[number];

/** Active means Nova use, a message sent, a client added, or a session held. */
export const ACTIVITY_FILTERS = [
  "active30d",
  "quiet30d",
  "neverActive",
] as const;
export type ActivityFilter = (typeof ACTIVITY_FILTERS)[number];

export const COACH_TAGS = [
  "founding",
  "featured",
  "ghl",
  "payouts",
  "feeExempt",
  "noLogin",
  "acceleratorGraduate",
] as const;
export type CoachTag = (typeof COACH_TAGS)[number];

/**
 * How a coach uses what we built, as one pickable filter. Separate from
 * `COACH_TAGS`, which are account attributes rather than behaviour.
 */
export const COACH_USE_FILTERS = [
  "coachingOs",
  "neverCoachingOs",
  "lifeDesign",
  "cihAuthor",
  "unanswered",
] as const;
export type CoachUseFilter = (typeof COACH_USE_FILTERS)[number];

export const COACH_DIRECTORY_SORTS = [
  "joined",
  "name",
  "lastActive",
  "quality",
  "novaLastUsed",
  "novaCost30d",
  "novaRenewal",
  "activeClients",
  "inquiries",
  "leads",
  "unanswered",
  "articles",
  // Orders by a coach's readiness score, so only staff who may read the
  // scores can ask for it.
  "readiness",
] as const;
export type CoachDirectorySort = (typeof COACH_DIRECTORY_SORTS)[number];

export const COACH_DIRECTORY_PAGE_SIZES = [25, 50, 100] as const;
export type CoachDirectoryPageSize =
  (typeof COACH_DIRECTORY_PAGE_SIZES)[number];

/** The window behind every "30 days" figure in the directory. */
export const ACTIVITY_WINDOW_DAYS = 30;

export const coachDirectoryFiltersSchema = z.object({
  search: z.string().trim().max(200).default(""),
  account: z.enum(ACCOUNT_STATUSES).optional(),
  listing: z.enum(LISTING_STATUSES).optional(),
  nova: z.enum(NOVA_STATES).optional(),
  source: z.enum(COACH_SOURCES).optional(),
  activity: z.enum(ACTIVITY_FILTERS).optional(),
  tag: z.enum(COACH_TAGS).optional(),
  use: z.enum(COACH_USE_FILTERS).optional(),
});

export const coachDirectoryExportInputSchema =
  coachDirectoryFiltersSchema.extend({
    sort: z.enum(COACH_DIRECTORY_SORTS).default("joined"),
    direction: z.enum(SORT_DIRECTIONS).default("desc"),
  });

export const coachDirectoryListInputSchema =
  coachDirectoryExportInputSchema.extend({
    page: z.number().int().min(1).default(1),
    pageSize: z.number().int().min(1).max(100).default(25),
  });

export const coachDirectorySummaryInputSchema =
  coachDirectoryFiltersSchema.pick({ search: true });

export type CoachDirectoryFilters = z.output<
  typeof coachDirectoryFiltersSchema
>;
export type CoachDirectoryFacet = Exclude<
  keyof CoachDirectoryFilters,
  "search"
>;
export type CoachDirectoryExportParams = z.output<
  typeof coachDirectoryExportInputSchema
>;
export type CoachDirectoryListInput = z.input<
  typeof coachDirectoryListInputSchema
>;
export type CoachDirectoryListParams = z.output<
  typeof coachDirectoryListInputSchema
>;

export interface CoachDirectoryRow {
  readonly id: string;
  /** The coach's internal user id, which feature-flag whitelists hold. */
  readonly userId: string | null;
  readonly username: string;
  readonly fullName: string;
  readonly email: string;
  readonly phone: string | null;
  readonly photoUrl: string | null;
  readonly coachingTitle: string | null;
  readonly city: string | null;
  readonly country: string | null;
  readonly website: string | null;
  readonly linkedinUrl: string | null;
  readonly instagramUrl: string | null;
  readonly joinedAt: string;
  readonly source: CoachSource;
  /** Has a Clerk login linked; coaches without one cannot sign in. */
  readonly hasLogin: boolean;
  readonly accountStatus: AccountStatus;
  readonly listingStatus: ListingStatus;
  /** 0–1, the same score the marketplace ranks by. */
  readonly qualityScore: number;
  readonly onboardingCompletedAt: string | null;
  readonly flags: {
    readonly founding: boolean;
    readonly featured: boolean;
    /** Has a GoHighLevel sub-account: Marketing Suite buyers and legacy GHL coaches. */
    readonly ghl: boolean;
    readonly payouts: boolean;
    readonly contractSigned: boolean;
    readonly feeExempt: boolean;
    readonly joinedCommunity: boolean;
  };
  readonly stripeCustomerId: string | null;
  readonly membership: {
    readonly plan: MembershipPlan;
    /** Raw Stripe status of the membership subscription, when one exists. */
    readonly status: string | null;
    readonly lifetime: boolean;
  };
  readonly nova: {
    readonly state: NovaState;
    /** Raw Stripe status of the Nova subscription, when one exists. */
    readonly status: string | null;
    /** The plan the tracked Nova subscription bills (`coaches.nova_plan_key`). */
    readonly planKey: PlanKey | null;
    readonly periodEnd: string | null;
    readonly cancelAtPeriodEnd: boolean;
    readonly trialStartedAt: string | null;
    readonly paidStartedAt: string | null;
    readonly lastUsedAt: string | null;
    /** The UTC day (`YYYY-MM-DD`) she last opened a NOVA page. */
    readonly lastDay: string | null;
    readonly cost30dUsd: number;
    readonly threads: number;
    readonly runs: number;
    readonly deliverables: number;
  };
  readonly activity: {
    readonly lastActiveAt: string | null;
    readonly activeClients: number;
    readonly totalClients: number;
    readonly sessions30d: number;
    readonly messagesSent30d: number;
  };
  /**
   * What reached the coach, rather than what she did. An "inquiry" is a
   * conversation somebody else opened, which covers a marketplace enquiry and
   * a client writing first alike — we cannot tell a lead from a client by the
   * thread, and an inquiry that later became a client is a success, not noise.
   * A "lead" is the opposite case: a person who shared their details on one of
   * the coach's assessment pages, so the coach opened no conversation either.
   */
  readonly inbox: {
    readonly inquiries: number;
    readonly inquiries30d: number;
    /** Every lead row the coach sees under Lead magnets, whatever its status. */
    readonly leads: number;
    readonly leads30d: number;
    /** Inquiries with no reply from the coach anywhere in the thread. */
    readonly unanswered: number;
    readonly messagesReceived30d: number;
    readonly lastReceivedAt: string | null;
  };
  /**
   * Which parts of the platform the coach actually uses. We record no page
   * views, so each of these is evidence of work done in a section, not of a
   * visit: a coach who opened a page and left looks the same as one who never
   * opened it.
   */
  readonly platform: {
    /** Latest client added, session held, or message sent — Nova excluded. */
    readonly coachingOsLastUsedAt: string | null;
    /** Entries the coach owns across her own Life by Design pages. */
    readonly lifeDesignItems: number;
    readonly lifeDesignLastUsedAt: string | null;
    readonly articlesPublished: number;
    readonly articlesTotal: number;
    readonly articleLastPublishedAt: string | null;
  };
  readonly referralCreditCents: number;
  /** Business Builder Accelerator standing. Graduation is a calendar day at UTC midnight. */
  readonly accelerator: {
    readonly cohort: number | null;
    readonly purchasedAt: string | null;
    readonly graduatedAt: string | null;
  };
}

export interface CoachDirectoryPage {
  readonly rows: readonly CoachDirectoryRow[];
  readonly total: number;
  /** The page actually returned — clamped into range when the request overshot. */
  readonly page: number;
  readonly pageSize: number;
}

export interface CoachDirectoryExport {
  readonly rows: readonly CoachDirectoryRow[];
  /** Every matching coach, which exceeds `rows.length` when the export was capped. */
  readonly total: number;
}

export interface CoachDirectorySummary {
  readonly total: number;
  readonly listed: number;
  readonly inReview: number;
  readonly novaPaying: number;
  readonly novaTrialing: number;
  readonly novaCanceling: number;
  readonly novaBillingIssue: number;
  readonly active30d: number;
  /** Coaches with at least one inquiry they never replied to. */
  readonly unanswered: number;
}
